import * as React from "react";
import {
  GridColDef,
  GridRowsProp,
  GridSortModel,
  GridValueGetterParams,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import {
  Box,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import NoResultsOverlay from "../../components/NoResultOverlay";
import { useTitle } from "../../hooks/useTitle";
import { useGetFinanceReportQuery } from "../../services/report.service";
import { FinancialReport } from "../../types/report";
import dayjs, { Dayjs } from "dayjs";
import SelectCompany from "../../components/SelectCompany";
import CompanyDrawer from "../company/CompanyDrawer";
import TalentDrawer from "../talent/TalentDrawer";
import ProjectDrawer from "../project/ProjectDrawer";
import { CardBase, GlassDataGrid, Button, DatePickerBase, OrangeLinearProgress } from "../../components/global";
import { Download as DownloadIcon } from "@mui/icons-material";

const initStartDate = dayjs().startOf("month");
const initEndDate = dayjs();

const FinanceReportPage = () => {
  useTitle("Finance Report");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [pageSize, setPageSize] = React.useState(100);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [company, setCompany] = React.useState<number>(0);
  const [startDate, setStartDate] = React.useState<Dayjs | null>(initStartDate);
  const [endDate, setEndDate] = React.useState<Dayjs | null>(initEndDate);

  // for drawers
  const [openCompany, setOpenCompany] = React.useState(false);
  const [currentCompany, setCurrentCompany] = React.useState(null);
  const [openTalent, setOpenTalent] = React.useState(false);
  const [currentTalent, setCurrentTalent] = React.useState(null);
  const [openProject, setOpenProject] = React.useState(false);
  const [currentProject, setCurrentProject] = React.useState(null);

  const onViewCompany = (item: any) => {
    setCurrentCompany(item);
    setOpenCompany(true);
  };

  const onViewTalent = (item: any) => {
    setCurrentTalent(item);
    setOpenTalent(true);
  };

  const onViewProject = (item: any) => {
    setCurrentProject(item);
    setOpenProject(true);
  };

  const { data, isLoading, isFetching } = useGetFinanceReportQuery({
    companyId: !!company ? company : undefined,
    start_date: startDate ? startDate.format("YYYY-MM-DD") : dayjs().startOf("month").format("YYYY-MM-DD"),
    end_date: endDate ? endDate.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
  });
  const rowCount = React.useMemo(() => data?.length, [data]);

  const rows: GridRowsProp | undefined = React.useMemo(
    () =>
      data?.map((it, _id) => ({
        ...it,
        id: _id,
      })),
    [data]
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "job",
        headerName: "Job",
        minWidth: isMobile ? 150 : isTablet ? 200 : 300,
        valueGetter: (
          params: GridValueGetterParams<undefined, FinancialReport>
        ) => `#${params.row.job_id} ${params.row.title}`,
        renderCell: (params: GridRenderCellParams<undefined, FinancialReport>) => (
          <Link
            variant="body2"
            component={"button"}
            onClick={() => onViewProject({ id: params.row.job_id })}
            sx={{
              color: "#F4A259",
              textDecoration: "none",
              fontWeight: 500,
              fontSize: isMobile ? "0.75rem" : "0.875rem",
              "&:hover": {
                textDecoration: "underline",
                color: "#E8944A",
              },
            }}
          >
            {params.value}
          </Link>
        ),
      },
      {
        field: "project_site_number",
        headerName: "Site Number",
        minWidth: isMobile ? 80 : 120,
      },
      {
        field: "location",
        headerName: "Client/Location",
        minWidth: isMobile ? 120 : isTablet ? 150 : 200,
        valueGetter: (
          params: GridValueGetterParams<undefined, FinancialReport>
        ) => `${params.row.location_name}`,
      },
      {
        field: "project_state",
        headerName: "Job State",
        minWidth: isMobile ? 80 : 100,
        headerAlign: "center",
        align: "center",
        valueGetter: (
          params: GridValueGetterParams<undefined, FinancialReport>
        ) => `${params.row.project_state}`,
      },
      {
        field: "company",
        headerName: "Company",
        minWidth: isMobile ? 120 : isTablet ? 150 : 200,
        headerAlign: "center",
        align: "center",
        valueGetter: (
          params: GridValueGetterParams<undefined, FinancialReport>
        ) => `${params.row.company_name}`,
        renderCell: (params: GridRenderCellParams<undefined, FinancialReport>) => (
          <Link
            variant="body2"
            component={"button"}
            onClick={() => onViewCompany({ id: params.row.company_id })}
            sx={{
              color: "#F4A259",
              textDecoration: "none",
              fontWeight: 500,
              fontSize: isMobile ? "0.75rem" : "0.875rem",
              "&:hover": {
                textDecoration: "underline",
                color: "#E8944A",
              },
            }}
          >
            {params.value}
          </Link>
        ),
      },
      {
        field: "start_date",
        headerName: "Start",
        minWidth: isMobile ? 120 : isTablet ? 150 : 200,
        headerAlign: "center",
        align: "center",
        valueGetter: (
          params: GridValueGetterParams<undefined, FinancialReport>
        ) => `${dayjs(params.row?.start_date).format("MMM DD, YYYY hh:mm A")}`,
      },
      {
        field: "percentage_uplift",
        headerName: "Company Uplift(%)",
        align: "center",
        minWidth: isMobile ? 100 : 150,
        valueGetter: (
          params: GridValueGetterParams<undefined, FinancialReport>
        ) => `${Number(params.row.percentage_uplift)?.toFixed(0) || "0"}%`,
      },
      {
        field: "talent_name",
        headerName: "Talent",
        minWidth: isMobile ? 100 : 150,
        headerAlign: "center",
        align: "center",
        valueGetter: (
          params: GridValueGetterParams<undefined, FinancialReport>
        ) => `#${params.row.talent_id} ${params.row.talent_name}`,
        renderCell: (params: GridRenderCellParams<undefined, FinancialReport>) => (
          <Link
            variant="body2"
            component={"button"}
            onClick={() => onViewTalent({ id: params.row.talent_id })}
            sx={{
              color: "#F4A259",
              textDecoration: "none",
              fontWeight: 500,
              fontSize: isMobile ? "0.75rem" : "0.875rem",
              "&:hover": {
                textDecoration: "underline",
                color: "#E8944A",
              },
            }}
          >
            {params.value}
          </Link>
        ),
      },
      {
        field: "type",
        headerName: "Contract",
        headerAlign: "center",
        align: "center",
        valueGetter: (
          params: GridValueGetterParams<undefined, FinancialReport>
        ) => `${params.row.type.toUpperCase()}`,
      },
      {
        field: "price",
        headerName: "Rate/Price",
        headerAlign: "center",
        align: "center",
        valueGetter: (
          params: GridValueGetterParams<undefined, FinancialReport>
        ) =>
          `$${params.row.type == "hourly"
            ? params.row.rate_hour
            : params.row.fix_price
          }`,
      },
      {
        field: "total_working",
        headerName: "Hour On Site",
        headerAlign: "center",
        align: "center",
      },
      {
        field: "total_earning",
        headerName: "Labor Cost",
        headerAlign: "center",
        align: "center",
        valueGetter: (
          params: GridValueGetterParams<undefined, FinancialReport>
        ) => `$${Number(params.row.total_earning)?.toFixed(2) || ""}`,
      },
      {
        field: "total_project_uplift",
        headerName: "Ticket Fee",
        headerAlign: "center",
        align: "center",
        valueGetter: (
          params: GridValueGetterParams<undefined, FinancialReport>
        ) => `${params.row.total_project_uplift?.toFixed(2) || ""}`,
      },
      {
        field: "total_additional_hours",
        headerName: "Additional Hours",
        headerAlign: "center",
        align: "center",
        valueGetter: (
          params: GridValueGetterParams<undefined, FinancialReport>
        ) => `$${params.row.total_additional_hours?.toFixed(2) || 0}`,
      },
      {
        field: "total_additional_hours_uplift",
        headerName: "AH-Fee",
        headerAlign: "center",
        align: "center",
        valueGetter: (
          params: GridValueGetterParams<undefined, FinancialReport>
        ) => `${params.row.total_additional_hours_uplift?.toFixed(2) || ""}`,
      },
      {
        field: "total_reimbursement",
        headerName: "Reimbursement",
        headerAlign: "center",
        align: "center",
        valueGetter: (
          params: GridValueGetterParams<undefined, FinancialReport>
        ) => `$${params.row.total_reimbursement?.toFixed(2) || 0}`,
      },
      {
        field: "total_reimbursement_uplift",
        headerName: "R-Fee",
        headerAlign: "center",
        align: "center",
        valueGetter: (
          params: GridValueGetterParams<undefined, FinancialReport>
        ) => `${params.row.total_reimbursement_uplift?.toFixed(2) || ""}`,
      },
      {
        field: "total_bonus",
        headerName: "Bonus",
        headerAlign: "center",
        align: "center",
        valueGetter: (
          params: GridValueGetterParams<undefined, FinancialReport>
        ) => `$${params.row.total_bonus?.toFixed(2) || 0}`,
      },
      {
        field: "total_bonus_uplift",
        headerName: "B-Fee",
        headerAlign: "center",
        align: "center",
        valueGetter: (
          params: GridValueGetterParams<undefined, FinancialReport>
        ) => `${params.row.total_bonus_uplift?.toFixed(2) || ""}`,
      },
      {
        field: "income",
        headerName: "P-Fees",
        headerAlign: "center",
        align: "center",
        valueGetter: (
          params: GridValueGetterParams<undefined, FinancialReport>
        ) => {
          const income =
            Number(params.row.fixed_price_fee || 0) +
            Number(params.row.total_project_uplift || 0) +
            Number(params.row.total_additional_hours_uplift || 0) +
            Number(params.row.total_reimbursement_uplift || 0) +
            Number(params.row.total_bonus_uplift || 0);
          return `$${income.toFixed(2) || ""}`;
        },
      },
      // {
      //   field: "payroll",
      //   headerName: "Total Cost",
      //   headerAlign: "center",
      //   align: "center",
      //   valueGetter: (
      //     params: GridValueGetterParams<undefined, FinancialReport>
      //   ) => {
      //     const totalEarning = Number(params.row.total_earning) || 0;
      //     const totalBonus = Number(params.row.total_bonus) || 0;
      //     const totalReimbursement =
      //       Number(params.row.total_reimbursement) || 0;
      //     const totalAdditionalHours =
      //       Number(params.row.total_additional_hours) || 0;

      //     const total =
      //       totalEarning +
      //       totalBonus +
      //       totalReimbursement +
      //       totalAdditionalHours;

      //     return `$${total.toFixed(2)}`;
      //   },
      // },
      {
        field: "gross",
        headerName: "G-Revenue",
        headerAlign: "center",
        align: "center",
        valueGetter: (
          params: GridValueGetterParams<undefined, FinancialReport>
        ) => {
          const totalEarning = Number(params.row.total_earning) || 0;
          const totalBonus = Number(params.row.total_bonus) || 0;
          const totalReimbursement =
            Number(params.row.total_reimbursement) || 0;
          const totalAdditionalHours =
            Number(params.row.total_additional_hours) || 0;
          const totalFixedUplift = Number(params.row.fixed_price_fee) || 0;

          const total =
            totalEarning +
            totalBonus +
            totalReimbursement +
            totalAdditionalHours +
            totalFixedUplift;

          return `$${total.toFixed(2)}`;
        },
      },
    ],
    [isMobile, isTablet, onViewProject, onViewCompany, onViewTalent]
  );

  const handleExport = React.useCallback(() => {
    if (!rows || rows.length === 0) return;

    // Get visible columns
    const visibleColumns = columns.filter((col) => !col.hide);

    // Create CSV headers
    const headers = visibleColumns.map((col) => `"${col.headerName}"`).join(",");

    // Create CSV rows
    const csvRows = rows.map((row: any) => {
      return visibleColumns
        .map((col) => {
          let value: string = "";
          if (col.valueGetter) {
            const params = { row } as any;
            const result = col.valueGetter(params);
            value = result != null ? String(result) : "";
          } else if (col.renderCell) {
            // For renderCell, try to get raw value
            const rawValue = (row as any)[col.field];
            value = rawValue ? String(rawValue) : "";
          } else {
            const rawValue = (row as any)[col.field];
            value = rawValue ? String(rawValue) : "";
          }
          // Escape quotes and wrap in quotes
          if (value && (value.includes(",") || value.includes('"') || value.includes("\n"))) {
            value = `"${value.replace(/"/g, '""')}"`;
          }
          return value || "";
        })
        .join(",");
    });

    // Combine header and rows
    const csvContent = [headers, ...csvRows].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Finance_Report_${dayjs().format("YYYY-MM-DD")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [rows, columns]);

  const handleReset = React.useCallback(() => {
    setCompany(0);
    setStartDate(initStartDate);
    setEndDate(initEndDate);
  }, []);

  return (
    <Box>
      <CardBase
        title="Finance Report"
        useGlassCard={false}
        sx={{ height: "85vh", p: 0 }}
        headerActions={
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
            flexWrap="wrap"
            sx={{ width: "100%", gap: 1.5 }}
          >
            <SelectCompany
              onChange={(val) => setCompany(val || 0)}
              value={company || 0}
              sx={{
                minWidth: { xs: "100%", sm: 250 },
                maxWidth: { xs: "100%", sm: 250 },
                width: { xs: "100%", sm: "auto" },
                marginTop: "0 !important",
                "& > div": {
                  marginTop: "0 !important",
                },
                "& > div > div": {
                  "& > div": {
                    minHeight: "32px !important",
                    height: "40px !important",
                  },
                  "& > div > div": {
                    padding: "4px 14px !important",
                    minHeight: "auto !important",
                  },
                },
              }}
            />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              <DatePickerBase
                label="Start Date"
                value={startDate}
                onChange={(value) => setStartDate(value)}
                inputFormat="MMM DD, YYYY"
                sx={{
                  minWidth: { xs: "100%", sm: 180 },
                  maxWidth: { xs: "100%", sm: 180 },
                  width: { xs: "100%", sm: "auto" },
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  px: { xs: 0, sm: 0.5 },
                  display: { xs: "none", sm: "block" },
                }}
              >
                to
              </Typography>
              <DatePickerBase
                label="End Date"
                value={endDate}
                onChange={(value) => setEndDate(value)}
                inputFormat="MMM DD, YYYY"
                sx={{
                  minWidth: { xs: "100%", sm: 180 },
                  maxWidth: { xs: "100%", sm: 180 },
                  width: { xs: "100%", sm: "auto" },
                }}
                minDate={startDate}
              />
            </Stack>
            <Box sx={{ flex: { xs: 0, sm: 1 }, minWidth: { xs: 0, sm: 20 } }} />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              <Button
                variant="text"
                onClick={handleReset}
                size="small"
                fullWidth={isMobile}
                sx={{
                  whiteSpace: "nowrap",
                  color: "#F4A259",
                  "&:hover": {
                    backgroundColor: "rgba(244, 162, 89, 0.08)",
                  },
                }}
              >
                Reset
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                disabled={!rows || rows.length === 0}
                size="small"
                fullWidth={isMobile}
                sx={{
                  whiteSpace: "nowrap",
                  borderColor: "#F4A259",
                  color: "#F4A259",
                  "&:hover": {
                    backgroundColor: "rgba(244, 162, 89, 0.08)",
                    borderColor: "#F4A259",
                  },
                  "&:disabled": {
                    borderColor: "rgba(244, 162, 89, 0.5)",
                    color: "rgba(244, 162, 89, 0.5)",
                  },
                }}
              >
                {isMobile ? "Export" : "Export CSV"}
              </Button>
            </Stack>
          </Stack>
        }
      >
        <GlassDataGrid
          rows={rows || []}
          columns={columns}
          rowCount={rowCount}
          pageSize={pageSize}
          rowsPerPageOptions={[100]}
          onPageSizeChange={(newSize: number) => setPageSize(newSize)}
          sortModel={sortModel}
          onSortModelChange={(newSortModel: GridSortModel) => setSortModel(newSortModel)}
          loading={isLoading || isFetching}
          components={{
            LoadingOverlay: OrangeLinearProgress,
            NoResultsOverlay: NoResultsOverlay,
          }}
          disableSelectionOnClick
          disableColumnFilter
          height={isMobile ? "calc(85vh - 200px)" : "calc(85vh - 80px)"}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "transparent",
              borderTop: "none",
              paddingLeft: isMobile ? theme.spacing(0.5) : theme.spacing(3),
              paddingRight: isMobile ? theme.spacing(0.5) : theme.spacing(3),
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "#F4A259",
                fontWeight: 600,
                fontSize: isMobile ? "0.75rem" : "0.875rem",
              },
              "& .MuiDataGrid-columnHeader": {
                color: "#F4A259",
                "& .MuiDataGrid-iconButtonContainer": {
                  color: "#F4A259",
                },
                "& .MuiDataGrid-sortIcon": {
                  color: "#F4A259",
                },
              },
            },
            "& .MuiDataGrid-cell": {
              paddingLeft: isMobile ? theme.spacing(0.5) : theme.spacing(3),
              paddingRight: isMobile ? theme.spacing(0.5) : theme.spacing(3),
              py: isMobile ? 1 : 2,
              fontSize: isMobile ? "0.75rem" : "0.875rem",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(244, 162, 89, 0.05)",
            },
          }}
        />
      </CardBase>
      <CompanyDrawer
        data={currentCompany!}
        open={openCompany}
        setOpen={setOpenCompany}
        forceRefresh={true}
      />
      <TalentDrawer
        data={currentTalent!}
        open={openTalent}
        setOpen={setOpenTalent}
        forceRefresh={true}
      />
      <ProjectDrawer
        data={currentProject!}
        open={openProject}
        setOpen={setOpenProject}
        forceRefresh={true}
      />
    </Box>
  );
};
export default FinanceReportPage;
